'use client';

import { useRef, useEffect, useCallback } from 'react';
import { useTutorial } from '@/hooks/useTutorial';
import { TutorialModal } from '@/components/onboarding/TutorialModal';
import { TutorialTooltip } from '@/components/onboarding/TutorialTooltip';

/**
 * Encapsulates the entire tutorial system:
 * - Modal shown once after login (cookie-based)
 * - Tooltip hint pointing at "Vídeos institucionais" link after modal close
 * - Tutorial button in header to re-open
 *
 * Rendered inside AppChrome so it's available on all pages with chrome.
 */
export function TutorialSystem() {
  const {
    showTutorial,
    showTooltipHint,
    closeTutorial,
    openTutorial,
    dismissTooltipHint,
    isAuthenticated,
  } = useTutorial();

  // Ref to the tooltip target element (found via data attribute)
  const tooltipTargetRef = useRef<HTMLElement | null>(null);

  // Find the target element after mount
  useEffect(() => {
    // Look for the tutorial button in the header via data attribute
    const el = document.querySelector('[data-tutorial-target="tutorial-btn"]') as HTMLElement | null;
    if (el) {
      tooltipTargetRef.current = el;
    }
  }, []);

  // Refresh ref when tooltip becomes visible
  useEffect(() => {
    if (showTooltipHint) {
      const el = document.querySelector('[data-tutorial-target="tutorial-btn"]') as HTMLElement | null;
      if (el) {
        tooltipTargetRef.current = el;
      }
    }
  }, [showTooltipHint]);

  // Listen for custom event to re-open tutorial from header button
  useEffect(() => {
    const handler = () => openTutorial();
    window.addEventListener('tutorial:open', handler);
    return () => window.removeEventListener('tutorial:open', handler);
  }, [openTutorial]);

  if (!isAuthenticated) return null;

  return (
    <>
      <TutorialModal isOpen={showTutorial} onClose={closeTutorial} />
      <TutorialTooltip
        targetRef={tooltipTargetRef}
        isVisible={showTooltipHint}
        onDismiss={dismissTooltipHint}
      />
    </>
  );
}
